import type React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { Tooltip } from '@components/ui/Tooltips'
import { CopyIcon } from '@oldComponents/ui/SvgIcons'

import { CopyToClipboard } from './CopyToClipboard'
import { CopyFeedbackIcon, copyFeedbackMessages, preventClick, useCopyFeedback } from './elements'

const WrapperDiv = styled.div<{ $noWrap: boolean }>`
  display: inline-flex;
  white-space: ${({ $noWrap }) => ($noWrap ? 'nowrap' : 'normal')};
  max-width: 100%;
`

const ButtonIconWrapper = styled.span`
  display: inherit;
  align-items: inherit;
  width: 16px;
  font-size: 16px;
  transition: opacity linear 0.1s;
  padding: 0;
`

const CopyButton = styled.button.attrs({
  type: 'button', //! NOTE - important when using it in form (in RFF first button submit the form)
})`
  display: inline-flex;
  vertical-align: middle;
  align-items: center;
  min-height: 16px;
  max-width: inherit;
  outline: 0;
  padding: 0;
  font: inherit;

  &,
  &:hover:not(:disabled),
  &:active:not(:disabled),
  &:focus:not(:disabled) {
    border: unset;
    background-color: unset;
  }

  & ${ButtonIconWrapper} {
    opacity: 0;
  }

  &:disabled {
    pointer-events: none;
    color: unset;
  }

  &:not(:disabled):hover,
  &:not(:disabled):focus {
    cursor: pointer;

    & ${ButtonIconWrapper} {
      opacity: 1;
    }
  }
`

interface CopyAndPasteButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode
  disabled?: boolean
  text?: Nullable<string>
  noWrap?: boolean
}

export function CopyAndPasteButton({
  children,
  text,
  className,
  onClick = preventClick,
  noWrap = false,
  ...rest
}: CopyAndPasteButtonProps) {
  const { onCopy, status } = useCopyFeedback()
  const { formatMessage } = useIntl()

  if (!text) {
    return <>{children}</>
  }

  return (
    <WrapperDiv className={className} $noWrap={noWrap}>
      <CopyToClipboard text={text} onCopy={onCopy}>
        <CopyButton onClick={onClick} {...rest}>
          {children}

          <Tooltip title={formatMessage(copyFeedbackMessages.copy)}>
            <ButtonIconWrapper>
              <CopyFeedbackIcon icon={<CopyIcon size={16} />} status={status} />
            </ButtonIconWrapper>
          </Tooltip>
        </CopyButton>
      </CopyToClipboard>
    </WrapperDiv>
  )
}
